// PnLChartsBase.tsx
import React, { useMemo, useEffect, useRef, useState } from "react";
import Chart, {
  Series,
  ArgumentAxis,
  ValueAxis,
  Tooltip,
  Legend,
  ZoomAndPan,
  ScrollBar,
  CommonAxisSettings,  CommonSeriesSettings,
  ConstantLine,
} from "devextreme-react/chart";
import { formatDate } from "devextreme/localization";
import { Label } from "devextreme-react/pie-chart";

import { numberFmt } from '../../utils/formatUtils';
import { baseUrl } from '../../hooks/useFetch';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type PageType = "dashboard" | "charts" | "history";

interface ViewPnl {
  elements: string[];
  data: Record<string, any>[];
}

interface PnlPayload {
  current_tax_year?: { columns: string[]; data: Record<string, any>[] };
  all_fys?: Record<string, { columns?: string[]; data?: Record<string, any>[] }>;
  var_limits?: { fy?: string; var_limit?: number }[];
}

interface PnLChartsBaseProps {
  page: PageType;
  cob_date?: Date | null;
  pnl_type?: string;
  element_type?: string;
  hide_legend_button?: boolean;
  height?: number;
}

interface ChartScaffoldProps {
  page: PageType;
  data: Record<string, any>[];
  elements: string[];
  showLegend: boolean;
  onLegendClick: (e: any) => void;
  legendItemsMapper: (items: any[]) => any[];
  seriesRenderer: (name: string) => React.ReactNode;
  valueAxisExtras: React.ReactNode;
  height?: number;
}

interface PnLChartsVarProps {
  page: PageType;
  viewPnl: ViewPnl;
  payload: PnlPayload | null;
  hide_legend_button?: boolean;
  height?: number;
}

interface PnLChartsLinesProps {
  page: PageType;
  viewPnl: ViewPnl;
  hide_legend_button?: boolean;
  height?: number;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const ukFY = (dateInput: Date | string | number): string | null => {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const fyStartThisYear = new Date(y, 3, 6);
  const fyEndYear = d >= fyStartThisYear ? y + 1 : y;
  return `FY-${String(fyEndYear).slice(-2)}`;
};

const toFY = (name: string | undefined | null): string | null => {
  const s = String(name || "");
  const yy = s.split("-")[0].trim();
  return yy ? `FY-${yy}` : null;
};

const base = (s: string): string =>
  String(s)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[-\s]*\d+\s*$/, "");

/** Return the FY end-year key for a given date (e.g. 2026 for any date in Apr 2025–Mar 2026). */
const cobFyEndYear = (d: Date): number => (d.getMonth() >= 3 ? d.getFullYear() + 1 : d.getFullYear());

/** Current FY end-year based on today. */
const currentFyEndYear = (): number => cobFyEndYear(new Date());

/**
 * Extract a single FY slice from all_fys and strip the "YY-" prefix from column names
 * so it looks like current_tax_year data.
 */
const extractFyView = (
  all_fys: Record<string, { columns?: string[]; data?: Record<string, any>[] }> | undefined,
  fyEndYear: number
): ViewPnl | null => {
  if (!all_fys) return null;
  const entry = all_fys[fyEndYear];
  if (!entry?.columns?.length || !entry?.data?.length) return null;

  const prefix = `${(fyEndYear % 100).toString().padStart(2, "0")}-`;
  const stripped = entry.columns.map((c) => (c.startsWith(prefix) ? c.slice(prefix.length) : c));
  const renameMap: Record<string, string> = {};
  entry.columns.forEach((c, i) => { renameMap[c] = stripped[i]; });

  const data = entry.data.map((row) => {
    const out: Record<string, any> = { cob_date: row.cob_date ?? null };
    entry.columns!.forEach((c) => { out[renameMap[c]] = row[c] ?? null; });
    return out;
  });
  return { elements: stripped, data };
};

const buildAllFysView = (all_fys: Record<string, { columns?: string[]; data?: Record<string, any>[] }> | undefined): ViewPnl => {
  if (!all_fys) return { elements: [], data: [] };

  const years = Object.keys(all_fys)
    .map(Number)
    .filter((y) => !Number.isNaN(y))
    .sort((a, b) => a - b);

  const elements = Array.from(new Set(years.flatMap((y) => all_fys[y]?.columns ?? []))).sort((a, b) =>
    a.localeCompare(b)
  );

  const data = years.flatMap((y) => all_fys[y]?.data ?? []);
  return { elements, data };
};

/** Align a COB date to the current FY range, matching the backend's align_to_current_fy. */
const alignCobToCurrentFy = (cob: Date): Date => {
  const now = new Date();
  const curFyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const curFyStart = new Date(curFyStartYear, 3, 1).getTime();

  const cobFyStartYear = cob.getMonth() >= 3 ? cob.getFullYear() : cob.getFullYear() - 1;
  const cobFyStart = new Date(cobFyStartYear, 3, 1).getTime();

  return new Date(curFyStart + (cob.getTime() - cobFyStart));
};

const filterTotalRMS = (allYears: ViewPnl, cob_date: Date | null | undefined): ViewPnl => {
  const columns = allYears?.elements ?? [];
  const data = allYears?.data ?? [];

  const elements = columns
    .filter((c) => /^\d{2}-Total RMS$/.test(c))
    .sort((a, b) => Number(a.slice(0, 2)) - Number(b.slice(0, 2)))
    .slice(-6);

  const latestCol = elements.reduce<string | null>((best, c) => {
    const yr = parseInt(String(c).split("-")[0], 10);
    if (Number.isNaN(yr)) return best;
    if (!best) return c;
    const bestYr = parseInt(String(best).split("-")[0], 10);
    return yr > bestYr ? c : best;
  }, null);

  const hasCutoff = cob_date != null;
  // Align the cutoff to the current FY, since backend aligns all data dates
  const cutoffTs = hasCutoff ? alignCobToCurrentFy(new Date(cob_date)).getTime() : Infinity;


  const rows = data.map((r) => {
    const t = r?.cob_date ? new Date(r.cob_date).getTime() : NaN;
    const afterCutoff = hasCutoff && Number.isFinite(t) && t > cutoffTs;

    return {
      cob_date: r.cob_date ?? null,
      ...Object.fromEntries(
        elements.map((c) => {
          if (c === latestCol && afterCutoff) return [c, null];
          return [c, r[c] ?? null];
        })
      ),
    };
  });

  return { elements, data: rows };
};

const mapLegendItems = (items: any[], page: PageType): any[] => {
  const mapDashboard = (it: any) => {
    const t = String(it.text || "");
    const moved = t.replace(
      /^\s*(\d{1,2})\s*-\s*Total RMS(.*)$/i,
      (_m: string, n: string, rest: string) => `FY-${n}${rest}`
    );
    if (moved !== t) return { ...it, text: moved };
    return { ...it, text: t.replace(/^\s*Total RMS(.*)$/i, (_m: string, rest: string) => `FY-${rest}`) };
  };

  const mapHistory = (it: any) => {
    const t = String(it.text || "");
    const m = t.match(/^\s*(\d+)\s*-\s*(.+)\s*$/);
    return m ? { ...it, text: `${m[2].trim()}-${m[1]}` } : it;
  };

  const mapped =
    page === "dashboard" ? items.map(mapDashboard) : page === "history" ? items.map(mapHistory) : items;

  return [...mapped].sort((a, b) =>
    String(a.text || "")
      .trim()
      .localeCompare(String(b.text || "").trim(), undefined, { sensitivity: "base", numeric: true })
  );
};

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

const usePnlPayload = ({ pnl_type, element_type }: { pnl_type: string; element_type: string }): PnlPayload | null => {
  const [payload, setPayload] = useState<PnlPayload | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const params = new URLSearchParams({ pnl_type, element_type });
        const res = await fetch(`${baseUrl}/pnl_plot?${params}`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setPayload(await res.json());
      } catch (err: any) {
        if (err.name !== "AbortError") console.error("Failed to load PnL data", err);
      }
    })();
    return () => ctrl.abort();
  }, [pnl_type, element_type]);

  return payload;
};

const useViewPnl = ({ payload, page, cob_date }: { payload: PnlPayload | null; page: PageType; cob_date?: Date | null }): ViewPnl => {
  return useMemo(() => {
    if (!payload) return { elements: [], data: [] };
    const { current_tax_year, all_fys } = payload;
    if (page === "charts") {
      // If the selected COB falls in a prior FY, pull that FY from all_fys
      if (cob_date) {
        const cobFy = cobFyEndYear(new Date(cob_date));
        if (cobFy !== currentFyEndYear()) {
          const fyView = extractFyView(all_fys, cobFy);
          if (fyView) return fyView;
        }
      }

      // Use current_tax_year if it has data
      const ctData = current_tax_year?.data ?? [];
      if (ctData.length > 0) {
        return { elements: current_tax_year?.columns ?? [], data: ctData };
      }

      // Fallback: no cob_date and current FY is empty (e.g. just started) —
      // show the most recent prior FY that has data
      if (all_fys) {
        const prevFy = currentFyEndYear() - 1;
        const fyView = extractFyView(all_fys, prevFy);
        if (fyView) return fyView;
      }

      return { elements: [], data: [] };
    }

    if (page === "history") {
      const copy = structuredClone(all_fys);
      if (copy) delete copy["2021"];
      return buildAllFysView(copy);
    }

    if (page === "dashboard") {
      return filterTotalRMS(buildAllFysView(all_fys), cob_date);
    }

    return { elements: [], data: [] };
  }, [payload, page, cob_date]);
};

const useLimitByFy = (payload: PnlPayload | null): Record<string, number | undefined> => {
  return useMemo(() => {
    const m: Record<string, number | undefined> = {};
    (payload?.var_limits || []).forEach((x) => {
      if (x?.fy != null) m[x.fy] = x.var_limit;
    });
    return m;
  }, [payload]);
};

/* ------------------------------------------------------------------ */
/*  Tooltip                                                            */
/* ------------------------------------------------------------------ */

const customizeTooltip = (info: any) => {
  const points = info.points || [info];
  const lines = [
    `<div style="text-align:center; white-space:pre;"><strong>${formatDate(
      points[0].argument,
      "dd-MMM"
    )}</strong></div>`,
    ...points.map((p: any) => {
      const display = p.seriesName;
      return `<div style="text-align:right; white-space:pre;">${display}:  ${numberFmt(p.value)}</div>`;
    }),
  ];
  return { html: lines.join("") };
};

/* ------------------------------------------------------------------ */
/*  ChartScaffoldPnL                                                   */
/* ------------------------------------------------------------------ */

const ChartScaffoldPnL: React.FC<ChartScaffoldProps> = ({
  page,
  data,
  elements,
  showLegend,
  onLegendClick,
  legendItemsMapper,
  seriesRenderer,
  valueAxisExtras,
  height,
}) => {
  const chartRef = useRef<any>(null);

  const now = new Date();
  const fyStartYear = now >= new Date(now.getFullYear(), 3, 1) ? now.getFullYear() : now.getFullYear() - 1;
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31, 23, 59, 59);

  return (
    <Chart
      ref={chartRef}
      dataSource={data}
      palette="Material"
      onLegendClick={onLegendClick}
      adaptiveLayout={{ width: 200, height: 250, keepLabels: true }}
      size={{ height: height ?? (page === "dashboard" ? 400 : 1000) }}
      redrawOnResize
    >
      <CommonAxisSettings grid={{ visible: true }} tick={{ visible: true }} label={{ overlappingBehavior: "stagger" }} />

      <ArgumentAxis
        argumentType="datetime"
        visualRange={{ startValue: fyStart, endValue: fyEnd }}
        wholeRange={{ startValue: fyStart, endValue: fyEnd }}
        label={{ font: { size: 14 }, customizeText: (e: any) => formatDate(e.value, "MMM dd") }}
      />

      <ValueAxis>
        {valueAxisExtras}
        <Label
          customizeText={(e: any) =>
            (e.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })
          }
        />
      </ValueAxis>

      {elements.map(seriesRenderer)}

      <ZoomAndPan argumentAxis="both" />
      <ScrollBar visible />
      <Tooltip enabled shared customizeTooltip={customizeTooltip} zIndex={2147483000} />
      <Legend
        customizeItems={legendItemsMapper}
        visible={showLegend}
        position="outside"
        orientation="horizontal"
        verticalAlignment="bottom"
        horizontalAlignment="center"
        columnCount={6}
        markerSize={10}
        itemTextPosition="right"
        paddingLeftRight={4}
        paddingTopBottom={2}
        font={{ size: 13 }}
      />
    </Chart>
  );
};


const defaultPctTooltip = (info: any) => {
  const m = info?.argument;
  const v = Number(info?.value);
  const s = info?.seriesName || "";
  return { text: `${s}\nMonth: ${m}\n${Number.isFinite(v) ? (v * 100).toFixed(2) + "%" : ""}` };
};

/* ------------------------------------------------------------------ */
/*  PnLChartsVar                                                       */
/* ------------------------------------------------------------------ */

const PnLChartsVar: React.FC<PnLChartsVarProps> = ({ page, viewPnl, payload, hide_legend_button, height }) => {
  const [showLegend, setShowLegend] = useState<boolean>(true);

  const latestFY = useMemo(() => {
    const fys = viewPnl.elements.map(toFY).filter(Boolean) as string[];
    if (fys.length === 0) return ukFY(Date.now());
    return fys.sort().pop()!;
  }, [viewPnl.elements]);

  const [selectedFY, setSelectedFY] = useState<string | null>(latestFY);

  useEffect(() => {
    if (latestFY && selectedFY && !viewPnl.elements.some((e) => toFY(e) === selectedFY)) {
      setSelectedFY(latestFY);
    }
  }, [latestFY]);

  const limitByFy = useLimitByFy(payload);

  const limitValue = selectedFY && limitByFy[selectedFY] != null ? limitByFy[selectedFY]! : null;

  const onLegendClick = (e: any) => {
    const raw = typeof e.target?.getName === "function" ? e.target.getName() : e.target?.name;
    const fy = toFY(raw);
    if (fy) setSelectedFY(fy);
    if (e.event?.preventDefault) e.event.preventDefault();
  };

  const seriesRenderer = (name: string) => {
    const fy = toFY(name);
    const visible = fy ? fy === selectedFY : false;

    return (
      <Series
        key={name}
        type="line"
        ignoreEmptyPoints
        argumentField="cob_date"
        valueField={name}
        name={name}
        visible={visible}
        hoverMode="includePoints"
        point={{ visible: false }}
        width={2}
      />
    );
  };

  const valueInMillions = (val: number): string =>
    (Number(val) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + " €M";

  return (
    <div style={{ width: "100%", height: "80%" }}>
      <ChartScaffoldPnL
        page={page}
        data={viewPnl.data}
        elements={viewPnl.elements}
        showLegend={showLegend}
        onLegendClick={onLegendClick}
        legendItemsMapper={(items) => mapLegendItems(items, page)}
        seriesRenderer={seriesRenderer}
        height={height}
        valueAxisExtras={
          limitValue != null ? (
            <ConstantLine
              value={limitValue}
              width={2}
              dashStyle="dash"
              color="yellow"
              extendAxis
              label={{ text: `Var limit ${valueInMillions(limitValue)}`, font: { color: "yellow" } }}
            />
          ) : null
        }
      />

      {!hide_legend_button && (
        <button onClick={() => setShowLegend((v) => !v)}>{showLegend ? "Hide legend" : "Show legend"}</button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  PnLChartsLines                                                     */
/* ------------------------------------------------------------------ */

const PnLChartsLines: React.FC<PnLChartsLinesProps> = ({ page, viewPnl, hide_legend_button, height }) => {
  const [showLegend, setShowLegend] = useState<boolean>(true);
  const defaultVisibleBases = useMemo(() => new Set(["market maker total", "sales total", "total rms"]), []);

  const onLegendClick = (e: any) => {
    const series = e.target;
    if (!series) return;
    series.isVisible() ? series.hide() : series.show();
  };

  const seriesRenderer = (name: string) => {
    const visible =
      page === "dashboard" || page === "history" ? /Total RMS/i.test(name) : defaultVisibleBases.has(base(name));

    return (
      <Series
        key={name}
        type="line"
        ignoreEmptyPoints
        argumentField="cob_date"
        valueField={name}
        name={name}
        visible={visible}
        hoverMode="includePoints"
        point={{ visible: false }}
        width={2}
      />
    );
  };

  return (
    <div style={{ width: "100%", height: "80%" }}>
      <ChartScaffoldPnL
        page={page}
        data={viewPnl.data}
        elements={viewPnl.elements}
        showLegend={showLegend}
        onLegendClick={onLegendClick}
        legendItemsMapper={(items) => mapLegendItems(items, page)}
        seriesRenderer={seriesRenderer}
        height={height}
        valueAxisExtras={null}
      />

      {!hide_legend_button && (
        <button onClick={() => setShowLegend((v) => !v)}>{showLegend ? "Hide legend" : "Show legend"}</button>
      )}
    </div>
  );
};


const PnLChartsBase: React.FC<PnLChartsBaseProps> = ({
  page,
  cob_date = undefined,
  pnl_type = "pnl_fytd",
  element_type = "all",
  hide_legend_button = false,
  height,
}) => {
  const payload = usePnlPayload({ pnl_type, element_type });
  const viewPnl = useViewPnl({ payload, page, cob_date });

  if (pnl_type === "standalone_var") {
    return (
      <PnLChartsVar
        page={page}
        viewPnl={viewPnl}
        payload={payload}
        hide_legend_button={hide_legend_button}
        height={height}
      />
    );
  }

  return (
    <PnLChartsLines
      page={page}
      viewPnl={viewPnl}
      hide_legend_button={hide_legend_button}
      height={height}
    />
  );
};

export default PnLChartsBase;
